import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Leaf,
  Wind,
  Brain,
  Apple,
  Heart,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  BookOpen,
  Mail,
  Sparkles,
} from "lucide-react";

const API_URL = window.location.origin;

// Section navigation links
const SECTION_NAV = [
  { name: "Home", path: "/benessere", icon: Leaf },
  { name: "Articoli", path: "/benessere/articoli", icon: BookOpen },
  { name: "Tecniche", path: "/benessere/tecniche", icon: Sparkles },
  { name: "Newsletter", path: "/benessere#newsletter", icon: Mail },
];

// Category navigation links
const CATEGORY_NAV = [
  { key: "respirazione", name: "Respirazione", icon: Wind, color: "from-cyan-500 to-blue-600" },
  { key: "meditazione", name: "Meditazione", icon: Brain, color: "from-purple-500 to-indigo-600" },
  { key: "alimentazione", name: "Alimentazione", icon: Apple, color: "from-green-500 to-emerald-600" },
  { key: "gestione_stress", name: "Gestione Stress", icon: Heart, color: "from-teal-500 to-cyan-600" },
];

// Icons mapping
const CATEGORY_ICONS = {
  respirazione: Wind,
  meditazione: Brain,
  alimentazione: Apple,
  gestione_stress: Heart,
};

const CATEGORY_COLORS = {
  respirazione: "from-cyan-500 to-blue-600",
  meditazione: "from-purple-500 to-indigo-600",
  alimentazione: "from-green-500 to-emerald-600",
  gestione_stress: "from-teal-500 to-cyan-600",
};

// Gallery images for each category
const CATEGORY_IMAGES = {
  meditazione: [
    {
      url: "https://customer-assets.emergentagent.com/job_3aa397d2-df3c-4e29-af8c-edf4212ba1cc/artifacts/tll0fuor_image.png",
      alt: "Anandamayi Ma - Santa indiana",
      caption: "Anandamayi Ma: Una delle più venerate figure spirituali indiane, che ha incarnato la saggezza vedica",
      isSpecial: true
    },
    {
      url: "https://customer-assets.emergentagent.com/job_3aa397d2-df3c-4e29-af8c-edf4212ba1cc/artifacts/l9k7r2rw_image.png",
      alt: "Donne in meditazione con ghirlande di calendula",
      caption: "Pratica meditativa tradizionale indiana"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_3aa397d2-df3c-4e29-af8c-edf4212ba1cc/artifacts/2nu838iq_image.png",
      alt: "Gruppo di meditazione con incenso",
      caption: "Meditazione di gruppo in ambiente raccolto"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_619ba940-2223-44a7-83b6-8c72a8e10f31/artifacts/5taqumtl_image.png",
      alt: "Meditazione in ashram con dipinto buddista e luce soffusa",
      caption: "Ambiente meditativo tradizionale"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_619ba940-2223-44a7-83b6-8c72a8e10f31/artifacts/vah3cf90_image.png",
      alt: "Meditazione con candele e incenso in penombra",
      caption: "Pratica con candele e incenso"
    }
  ],
  respirazione: [
    {
      url: "https://customer-assets.emergentagent.com/job_619ba940-2223-44a7-83b6-8c72a8e10f31/artifacts/npjocong_image.png",
      alt: "Yoga su terrazzo con vista mare in Grecia",
      caption: "Yoga e respirazione vista mare - Grecia"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_619ba940-2223-44a7-83b6-8c72a8e10f31/artifacts/kvih0yzo_image.png",
      alt: "Yoga in tempio antico al tramonto",
      caption: "Pratica in tempio al tramonto"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_619ba940-2223-44a7-83b6-8c72a8e10f31/artifacts/6btuj97d_image.png",
      alt: "PRANA - l'energia vitale del respiro",
      caption: "PRANA - il soffio della vita"
    }
  ],
  alimentazione: [
    {
      url: "https://customer-assets.emergentagent.com/job_b4010d26-5e3f-47a1-856b-475f3bea0f94/artifacts/olmd4zrh_image.png",
      alt: "Frutti di bosco freschi - mirtilli, lamponi, more, fragole",
      caption: "Superfoods: il potere antiossidante dei frutti di bosco"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_b4010d26-5e3f-47a1-856b-475f3bea0f94/artifacts/465aorwg_image.png",
      alt: "Meal prep vegetariano colorato con verdure fresche",
      caption: "Meal prep vegetariano - preparazione settimanale"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_b4010d26-5e3f-47a1-856b-475f3bea0f94/artifacts/7uer87b2_image.png",
      alt: "Donna che prepara insalata fresca in cucina luminosa",
      caption: "La gioia di cucinare cibi sani e naturali"
    }
  ],
  gestione_stress: [
    {
      url: "https://customer-assets.emergentagent.com/job_b4010d26-5e3f-47a1-856b-475f3bea0f94/artifacts/20dlxzsc_image.png",
      alt: "Piedi scalzi in ruscello nella foresta - Earthing",
      caption: "Earthing: il potere di camminare scalzi nella natura"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_b4010d26-5e3f-47a1-856b-475f3bea0f94/artifacts/kcn7b6up_image.png",
      alt: "Struttura molecolare degli ioni negativi",
      caption: "Ioni negativi: molecole del benessere"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_619ba940-2223-44a7-83b6-8c72a8e10f31/artifacts/ltay33nv_image.png",
      alt: "Yoga nella foresta sotto un grande albero",
      caption: "Yoga immersi nella natura"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_619ba940-2223-44a7-83b6-8c72a8e10f31/artifacts/nslfzmg8_image.png",
      alt: "Meditazione yoga su roccia nel ruscello della foresta",
      caption: "Equilibrio e connessione con la natura"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_619ba940-2223-44a7-83b6-8c72a8e10f31/artifacts/ge63p9sd_image.png",
      alt: "Abbracciare un albero gigante - Forest Bathing",
      caption: "Abbracciare gli alberi - Tree Hugging"
    }
  ]
};

// Image Gallery Component with zoom effect
const ImageGallery = ({ images }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  if (!images || images.length === 0) return null;
  
  // Calculate optimal columns based on image count
  const getGridClass = () => {
    const count = images.length;
    if (count === 2) return 'md:grid-cols-2';
    if (count === 4) return 'md:grid-cols-2 lg:grid-cols-4';
    return 'md:grid-cols-3'; // default for 3 images
  };

  return (
    <>
      {/* Gallery Grid */}
      <div className={`grid grid-cols-1 ${getGridClass()} gap-4 my-8`} data-testid="image-gallery">
        {images.map((image, index) => (
          <div 
            key={index}
            className="group relative overflow-hidden rounded-2xl shadow-lg cursor-pointer"
            onClick={() => setSelectedImage(image)}
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
            </div>
            {/* Overlay on hover */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="text-white text-sm font-medium">{image.caption}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            ×
          </button>
          <div className="max-w-5xl max-h-[90vh] overflow-hidden rounded-xl">
            <img 
              src={selectedImage.url}
              alt={selectedImage.alt}
              className="w-full h-full object-contain"
            />
            <p className="text-white text-center mt-4 text-lg">{selectedImage.caption}</p>
          </div>
        </div>
      )}
    </>
  );
};

// Medical Disclaimer Component
const MedicalDisclaimer = () => (
  <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg my-6" data-testid="medical-disclaimer">
    <div className="flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <p className="text-amber-800 text-sm leading-relaxed">
        <strong>Disclaimer medico:</strong> I contenuti presenti in questa piattaforma hanno finalità 
        esclusivamente informativa e divulgativa. Non sostituiscono il parere medico. Le tecniche 
        e le informazioni presentate possono rappresentare un supporto al benessere generale ma 
        devono sempre essere integrate con le indicazioni del proprio medico.
      </p>
    </div>
  </div>
);

// Section Navigation Component
const SectionNavigation = () => (
  <nav className="bg-white/90 backdrop-blur-md border-b border-slate-100 py-3" data-testid="section-navigation">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
        <span className="text-xs text-slate-500 uppercase tracking-wide mr-2 whitespace-nowrap">Sezioni:</span>
        {SECTION_NAV.map((section) => {
          const IconComponent = section.icon;
          return (
            <Link 
              key={section.path} 
              to={section.path}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-teal-100 text-slate-700 hover:text-teal-700 rounded-full text-sm font-medium transition-colors whitespace-nowrap"
            >
              <IconComponent className="w-4 h-4" />
              {section.name}
            </Link>
          );
        })}
      </div>
    </div>
  </nav>
);

// Category Navigation Component
const CategoryNavigation = ({ currentCategory }) => (
  <nav className="bg-gradient-to-r from-slate-800 to-slate-900 py-4" data-testid="category-navigation">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
        <span className="text-xs text-slate-400 uppercase tracking-wide mr-2 whitespace-nowrap">Categorie:</span>
        {CATEGORY_NAV.map((cat) => {
          const isActive = currentCategory === cat.key;
          const IconComponent = cat.icon;
          return (
            <Link 
              key={cat.key} 
              to={`/benessere/tecniche/${cat.key}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                isActive 
                  ? `bg-gradient-to-r ${cat.color} text-white shadow-lg` 
                  : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600 hover:text-white'
              }`}
            >
              <IconComponent className="w-4 h-4" />
              {cat.name}
            </Link>
          );
        })}
      </div>
    </div>
  </nav>
);

export default function TecnicheBenessere() {
  const { categoria } = useParams();
  const [tecniche, setTecniche] = useState(null);
  const [selectedCategoria, setSelectedCategoria] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTecniche();
  }, [categoria]);

  const loadTecniche = async () => {
    setLoading(true);
    try {
      if (categoria) {
        // Load specific category
        const response = await fetch(`${API_URL}/api/public/tecniche-benessere/${categoria}`);
        if (response.ok) {
          const data = await response.json();
          setSelectedCategoria(data.data);
        }
      } else {
        // Load all categories
        const response = await fetch(`${API_URL}/api/public/tecniche-benessere`);
        if (response.ok) {
          const data = await response.json();
          setTecniche(data.tecniche);
        }
      }
    } catch (error) {
      console.error("Errore caricamento tecniche:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  // Single category view
  if (categoria && selectedCategoria) {
    const IconComponent = CATEGORY_ICONS[categoria] || Heart;
    const colorClass = CATEGORY_COLORS[categoria] || "from-teal-500 to-cyan-600";

    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="tecniche-categoria-page">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/benessere" className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                  <Leaf className="w-5 h-5 text-white" />
                </div>
                <span className="font-serif text-lg font-semibold text-slate-800">La Scienza del Benessere</span>
              </Link>
              <Link to="/login">
                <Button variant="outline" size="sm" className="rounded-full">
                  Area Riservata
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Section Navigation */}
        <SectionNavigation />
        
        {/* Category Navigation */}
        <CategoryNavigation currentCategory={categoria} />

        {/* Hero Section */}
        <section className={`relative py-16 bg-gradient-to-r ${colorClass}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/benessere/tecniche" className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tutte le Tecniche
            </Link>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <span className="text-4xl">{selectedCategoria.icon}</span>
              </div>
              <div>
                <h1 className="font-serif text-3xl sm:text-4xl font-bold text-white" data-testid="categoria-title">
                  {selectedCategoria.name}
                </h1>
                <p className="text-white/80 mt-1">{selectedCategoria.description}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Medical Disclaimer */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <MedicalDisclaimer />
        </div>

        {/* Image Gallery for this category */}
        {CATEGORY_IMAGES[categoria] && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="font-serif text-2xl font-bold text-slate-800 mb-4 text-center">
              Ispirazioni visive per la tua pratica
            </h3>
            <ImageGallery images={CATEGORY_IMAGES[categoria]} />
          </div>
        )}

        {/* Techniques List */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-8" data-testid="tecniche-list">
            {selectedCategoria.tecniche.map((tecnica, index) => (
              <Card key={tecnica.id} className="overflow-hidden border-0 shadow-lg" data-testid={`tecnica-${tecnica.id}`}>
                <CardHeader className={`bg-gradient-to-r ${colorClass} text-white`}>
                  <CardTitle className="flex items-center gap-3">
                    <span className="text-2xl">{index + 1}.</span>
                    {tecnica.titolo}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  {/* Description - with markdown-like formatting */}
                  <div className="prose prose-slate max-w-none">
                    <div className="text-slate-700 text-lg leading-relaxed whitespace-pre-line">
                      {tecnica.descrizione.split('**').map((part, idx) => 
                        idx % 2 === 1 ? <strong key={idx}>{part}</strong> : part
                      )}
                    </div>
                  </div>

                  {/* Technique Images */}
                  {tecnica.immagini && tecnica.immagini.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {tecnica.immagini.map((img, imgIdx) => (
                        <div key={imgIdx} className="rounded-xl overflow-hidden shadow-md">
                          <img 
                            src={img.url} 
                            alt={img.alt} 
                            className="w-full h-48 object-cover"
                            loading="lazy"
                          />
                          {img.caption && (
                            <p className="p-3 text-sm text-slate-600 bg-slate-50 text-center">
                              {img.caption}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Scientific Explanation */}
                  <div className="bg-slate-50 rounded-xl p-5">
                    <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                      <Brain className="w-5 h-5 text-purple-600" />
                      Spiegazione Scientifica
                    </h4>
                    <div className="text-slate-600 leading-relaxed whitespace-pre-line">
                      {tecnica.spiegazione_scientifica.split('**').map((part, idx) => 
                        idx % 2 === 1 ? <strong key={idx} className="text-slate-800">{part}</strong> : part
                      )}
                    </div>
                  </div>

                  {/* Instructions - Step by Step */}
                  <div className="bg-teal-50 rounded-xl p-5">
                    <h4 className="font-semibold text-teal-800 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-teal-600" />
                      Come Praticare - Guida Passo-Passo
                    </h4>
                    <div className="text-teal-700 leading-relaxed whitespace-pre-line">
                      {tecnica.indicazioni.split('**').map((part, idx) => 
                        idx % 2 === 1 ? <strong key={idx} className="text-teal-900">{part}</strong> : part
                      )}
                    </div>
                  </div>

                  {/* Detailed Benefits (if available) */}
                  {tecnica.benefici_dettagliati && (
                    <div className="bg-green-50 rounded-xl p-5">
                      <h4 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
                        <Heart className="w-5 h-5 text-green-600" />
                        Benefici Spiegati
                      </h4>
                      <div className="space-y-4">
                        {Object.entries(tecnica.benefici_dettagliati).map(([beneficio, spiegazione], i) => (
                          <div key={i} className="border-l-4 border-green-400 pl-4">
                            <h5 className="font-semibold text-green-700 mb-1">{beneficio}</h5>
                            <p className="text-green-600 text-sm leading-relaxed">{spiegazione}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Simple Benefits List (fallback) */}
                  {tecnica.benefici && !tecnica.benefici_dettagliati && (
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-3">Benefici:</h4>
                      <div className="flex flex-wrap gap-2">
                        {tecnica.benefici.map((beneficio, i) => (
                          <Badge key={i} variant="secondary" className="bg-green-100 text-green-700 px-3 py-1">
                            ✓ {beneficio}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Medical Disclaimer Bottom */}
          <MedicalDisclaimer />

          {/* CTA */}
          <div className="text-center mt-12">
            <Link to="/benessere/tecniche">
              <Button size="lg" className="rounded-full bg-gradient-to-r from-teal-500 to-cyan-600">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Esplora altre Tecniche
              </Button>
            </Link>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-8 mt-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} La Scienza del Benessere Applicata
            </p>
            <p className="text-slate-500 text-xs mt-2">
              Le informazioni presentate non sostituiscono il parere medico.
            </p>
          </div>
        </footer>
      </div>
    );
  }

  // All categories view
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="tecniche-home-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/benessere" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              <span className="font-serif text-lg font-semibold text-slate-800">La Scienza del Benessere</span>
            </Link>
            <Link to="/login">
              <Button variant="outline" size="sm" className="rounded-full">
                Area Riservata
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Section Navigation */}
      <SectionNavigation />

      {/* Hero Section with Background Image */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/06b77105-3c32-4871-969f-a30c12ceda3f/images/fd31fe9699988df621b109d2441744e22c2f1dc55f5c278afe90ccd988051825.png"
            alt="Wellness Science Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-600/90 to-cyan-600/80" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-white/20 text-white hover:bg-white/20 mb-6 px-4 py-1.5">
            <Heart className="w-4 h-4 mr-2" />
            Benessere Psicofisico
          </Badge>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="tecniche-hero-title">
            Tecniche di Benessere
          </h1>
          <p className="text-white/90 text-lg max-w-2xl mx-auto">
            Scopri pratiche scientificamente validate per migliorare il tuo benessere quotidiano. 
            Respirazione, meditazione, alimentazione e gestione dello stress.
          </p>
        </div>
      </section>

      {/* Scientific Sources Section */}
      <section className="bg-gradient-to-r from-slate-800 to-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="lg:w-1/3">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/06b77105-3c32-4871-969f-a30c12ceda3f/images/f1a84c99d0af173d88be8b2bfee259ad76efaee79c9284e59f84add062f77e6e.png"
                alt="Ricerca Scientifica Globale"
                className="w-full rounded-2xl shadow-2xl"
              />
            </div>
            <div className="lg:w-2/3 text-center lg:text-left">
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-4">
                Contenuti Basati su Ricerca Scientifica
              </h2>
              <p className="text-slate-300 mb-6 leading-relaxed">
                I nostri articoli e tecniche sono elaborati analizzando le più recenti pubblicazioni 
                scientifiche internazionali. Ci avvaliamo di fonti autorevoli per garantirti 
                informazioni accurate e aggiornate.
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                <Badge className="bg-blue-600/20 text-blue-300 border border-blue-500/30 px-4 py-2">
                  PubMed
                </Badge>
                <Badge className="bg-green-600/20 text-green-300 border border-green-500/30 px-4 py-2">
                  Google Scholar
                </Badge>
                <Badge className="bg-purple-600/20 text-purple-300 border border-purple-500/30 px-4 py-2">
                  Nature
                </Badge>
                <Badge className="bg-amber-600/20 text-amber-300 border border-amber-500/30 px-4 py-2">
                  Science Direct
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Medical Disclaimer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <MedicalDisclaimer />
      </div>

      {/* Categories Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="categories-grid">
          {tecniche && Object.entries(tecniche).map(([key, categoria]) => {
            const IconComponent = CATEGORY_ICONS[key] || Heart;
            const colorClass = CATEGORY_COLORS[key] || "from-teal-500 to-cyan-600";
            
            return (
              <Link key={key} to={`/benessere/tecniche/${key}`}>
                <Card className="group h-full hover:shadow-2xl transition-all duration-300 overflow-hidden border-0" data-testid={`category-card-${key}`}>
                  <div className={`h-3 bg-gradient-to-r ${colorClass}`} />
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${colorClass} flex items-center justify-center text-3xl group-hover:scale-110 transition-transform`}>
                        {categoria.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-slate-800 group-hover:text-teal-600 transition-colors">
                          {categoria.name}
                        </h3>
                        <p className="text-slate-500 mt-1 text-sm">
                          {categoria.description}
                        </p>
                      </div>
                      <ChevronRight className="w-6 h-6 text-slate-300 group-hover:text-teal-600 group-hover:translate-x-1 transition-all" />
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-sm text-slate-600">
                        <span className="font-semibold text-teal-600">{categoria.tecniche.length}</span> tecniche disponibili
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {categoria.tecniche.slice(0, 3).map((t, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {t.titolo.split(' ').slice(0, 2).join(' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Newsletter CTA */}
        <div className="mt-16 bg-gradient-to-r from-purple-600 to-indigo-700 rounded-3xl p-8 md:p-12 text-center">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-white mb-4">
            Vuoi ricevere nuove tecniche e consigli?
          </h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            Iscriviti alla newsletter per ricevere aggiornamenti su nuove tecniche, 
            articoli scientifici e consigli pratici per il tuo benessere.
          </p>
          <Link to="/benessere#newsletter">
            <Button size="lg" className="rounded-full bg-white text-purple-700 hover:bg-purple-50">
              Iscriviti alla Newsletter
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Medical Disclaimer Bottom */}
        <MedicalDisclaimer />
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} La Scienza del Benessere Applicata
          </p>
          <p className="text-slate-500 text-xs mt-2">
            Le informazioni presentate non sostituiscono il parere medico.
          </p>
        </div>
      </footer>
    </div>
  );
}
